import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import SeoHead from "./SiteSeo";
import { SmartLink } from "./SiteHeader";
import { Intro, FaqSection, FinalCta } from "./sections";
import { Testimonials } from "./Testimonials";
import { RatesShell, RatesHero, IntentBand, Shortlist, ForecastStrip, FixedVsVariable, RateWatchBand, GoDeeper, RatesSteps, StickyHoldBar, TextMyRates, cityRatesIntro, money, fillPlace } from "./RatesChrome";
import HUB from "./content/ratesHub.json";
import { SocialThreadSection, UNCLE_THREAD } from "./UnclePosts";
import FunnelDrawer from "../pages/public/FunnelDrawer";
import { publicApi } from "../pages/public/api";

const pct = (n) => (n == null ? "—" : `${n > 0 ? "+" : ""}${n}%`);
const presetFor = (t) => (/renew|refinanc|switch|variable/i.test(t) ? "renew_refi" : "buying");

// City rates page on the Rates design: shared hero/intent/shortlist + city-only content (market, scenarios, local FAQs).
export default function CityRatesV2({ data }) {
  const { slug, name: C } = data;
  const navigate = useNavigate();
  const [rates, setRates] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(true);
  const [x, setX] = useState({});
  const [drawer, setDrawer] = useState({ open: false, config: { mode: "full" } });
  useEffect(() => {
    publicApi.get("/featured-rates").then((r) => setRates(r.data)).catch(() => {}).finally(() => setRatesLoading(false));
    const grab = (k, url) => publicApi.get(url).then((r) => setX((s) => ({ ...s, [k]: r.data }))).catch(() => {});
    grab("desk", "/decision-desk"); grab("hist", `/market-snapshot/${slug}/history`); grab("insight", "/insights/general"); grab("outlook", "/fixed-outlook"); grab("reading", "/rates-reading"); grab("snap", `/market-snapshot/${slug}`);
  }, [slug]);
  const headlineRate = useMemo(() => {
    const cards = (rates?.cards || []).filter((c) => c.available && c.rate != null);
    return cards.length ? cards.reduce((a, b) => (a.rate <= b.rate ? a : b)) : null;
  }, [rates]);
  const openDrawer = (cfg = { mode: "full" }) => setDrawer({ open: true, config: { mode: "full", city: C, ...cfg } });
  const faqs = (data.faqs || []).map((f) => [f.q, f.a]);
  const updatedLabel = new Date(rates?.updated_at_iso || data.updated_at).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" });
  return (
    <RatesShell>
      <SeoHead title={data.title} description={data.description} path={`/${slug}/mortgage-rates/`} noindex={!data.indexable} />
      <Helmet><script type="application/ld+json">{JSON.stringify(data.schema)}</script></Helmet>
      <div data-testid={`city-rates-${slug}`}>
        <nav className="wrap crumbs" aria-label="Breadcrumb" data-testid="city-rates-breadcrumb"><SmartLink href="/">Home</SmartLink><span>›</span><SmartLink href={`/${slug}/`}>{C}</SmartLink><span>›</span><span aria-current="page">Mortgage rates</span></nav>
        <RatesHero place={C} headlineRate={headlineRate} rates={rates} ratesLoading={ratesLoading} boc={x.desk?.boc} compareHref={`/compare-all?city=${slug}`} eyebrow={`${C} mortgage rates · live from lender desks · updated ${updatedLabel}`} intro={cityRatesIntro(C)} />
        <IntentBand onIntent={(p) => openDrawer({ ...p, entryCta: "city_intent" })} />
        <Shortlist rates={rates} ratesLoading={ratesLoading} openDrawer={openDrawer} onCompareAll={() => navigate(`/compare-all?city=${slug}`)} termBase={`/${slug}/mortgage-rates`} intro={fillPlace(HUB.shortlist.city_text, C)} />
        <ForecastStrip insight={x.insight} outlook={x.outlook} boc={x.desk?.boc} place={C} />
        <FixedVsVariable place={C} onCta={() => openDrawer({ entryCta: "city_fvx" })} />
        <CityMarket data={data} snap={x.snap} hist={x.hist} C={C} slug={slug} />
        <CityScenarios data={data} C={C} openDrawer={openDrawer} />
        <SocialThreadSection eyebrow={fillPlace(HUB.uncle.eyebrow, C)} heading={<>{HUB.uncle.heading} <em>{HUB.uncle.heading_em}</em></>} text={fillPlace(HUB.uncle.text, C)} thread={UNCLE_THREAD} commenter={{ name: "your.uncle", avatar: "/brand/uncle-avatar.jpg" }} post={{ image: "/brand/uncle-bbq-post.jpg", tag: "Free mortgage rate advice", headline: "Available at every barbecue.", caption: "Free mortgage rate advice: available at every barbecue, every long weekend, forever 🍔\n\nWhat's the wildest rate \"advice\" you've gotten from a relative? Drop it below 👇 We reply to all of them.\n\n#MortgageRates #AlbertaMortgage #LongWeekend #WinningInLife" }} pinned={{ lead: HUB.uncle.pinned_lead, text: HUB.uncle.pinned_text, likes: 1208 }} compose="Ask us what your rate actually is…" testId="uncle-thread" />
        <RateWatchBand place={C} />
        <RatesSteps onCta={() => openDrawer({ entryCta: "how_it_works" })} />
        <GoDeeper reading={x.reading} />
        {faqs.length > 0 && <FaqSection items={faqs} eyebrow={fillPlace(HUB.faq.eyebrow, C)} heading={HUB.faq.heading} />}
        <Testimonials topics={["rates", "renewal", "pre-approval"]} city={C} />
        <FinalCta text={fillPlace(HUB.final_city, C)} />
        <StickyHoldBar outlook={x.outlook} place={C} />
        <TextMyRates source={`${slug}_rates_exit`} place={C} />
      </div>
      <FunnelDrawer open={drawer.open} onClose={() => setDrawer((d) => ({ ...d, open: false }))} config={drawer.config} />
    </RatesShell>
  );
}

const TYPE_ROWS = [["detached", "Detached"], ["semi_detached", "Semi-detached"], ["row", "Townhouse / row"], ["apartment", "Condo / apartment"]];

function CityMarket({ data, snap, hist, C, slug }) {
  const paras = data.market_overview?.length ? data.market_overview : data.blurb ? [data.blurb] : [];
  const tot = snap?.monthly?.total;
  if (!paras.length && !tot) return null;
  const lev = tot?.months_of_supply > 4 ? "Buyers have leverage" : tot?.months_of_supply < 2.5 ? "Sellers have leverage" : "Balanced market";
  return (
    <section className="rt-city" id="market" data-testid="city-market">
      <div className="wrap">
        <div className="rt-city-grid">
          <div className="rt-city-prose">
            <div className="eyebrow">{fillPlace(HUB.city_market.eyebrow, C)}</div>
            <h2 className="display">{fillPlace(HUB.city_market.heading, C)} <em>{HUB.city_market.heading_em}</em></h2>
            {paras.map((p, i) => <p key={i} data-testid={`city-market-p${i}`}>{p}</p>)}
          </div>
          {tot && (
            <aside className="rt-city-stats" data-testid="city-market-stats">
              <span className="rt-why-k">{snap.scope_label || C} · {snap.period_label}</span>
              <div><span>Average price</span><b>{money(tot.avg_price)}</b><small>{pct(tot.avg_price_yoy)} vs last year{hist?.mom_pct != null ? ` · ${pct(hist.mom_pct)} vs last month` : ""}</small></div>
              <Sparkline points={hist?.points} />
              <div><span>Homes sold</span><b>{tot.sales?.toLocaleString("en-CA")}</b><small>{pct(tot.sales_yoy)} vs last year</small></div>
              <div><span>Months of supply</span><b>{tot.months_of_supply}</b><small>{lev}</small></div>
              <small className="rt-city-src">Source: {snap.source_label} · <SmartLink href={`/${slug}/`}>{fillPlace(HUB.city_market.more, C)}</SmartLink></small>
            </aside>
          )}
        </div>
        <CityPaymentTable snap={snap} C={C} />
      </div>
    </section>
  );
}

function CityPaymentTable({ snap, C }) {
  const [mode, setMode] = useState("minimum");
  const b = snap?.buying;
  if (!b?.scenarios?.length) return null;
  const total = b.scenarios.find((s) => s.key === mode);
  const rows = TYPE_ROWS.map(([k, label]) => [label, b.by_type?.[k]?.[mode], snap.monthly?.[k]?.avg_price]).filter(([, m]) => m);
  const around = snap.is_proxy ? `around ${C}` : `in ${C}`;
  return (
    <div className="rt-buy" data-testid="city-payment-table">
      <div className="rt-buy-bar">
        <div><span className="rt-why-k">What today's rate means on a home {around}</span><p>We took what actually sold {snap.is_proxy ? "across Alberta" : `in ${snap.area_name}`} in {snap.period_label}, applied today's {b.rate.toFixed(2)}% over {b.amort_years} years, and worked out the down payment, the monthly payment and the household income the stress test wants to see.</p></div>
        <div className="snap-toggle" role="group" aria-label="Down payment scenario" data-testid="city-down-toggle">
          {[["minimum", "Minimum down"], ["conventional", "20% down"]].map(([k, l]) => <button type="button" key={k} className={mode === k ? "on" : ""} aria-pressed={mode === k} onClick={() => setMode(k)} data-testid={`city-down-toggle-${k}`}>{l}</button>)}
        </div>
      </div>
      <div className="rt-market-table rt-buy-table" role="table" aria-label={`Down payment, monthly payment and income to qualify by property type in ${C}`}>
        <div className="rt-market-head" role="row"><span>Property type</span><span>Average price</span><span>Down payment</span><span>Monthly payment</span><span>Income to qualify</span></div>
        {total && <Row label="All homes" price={b.price} sc={total} bold />}
        {rows.map(([label, sc, price]) => <Row key={label} label={label} price={price} sc={sc} />)}
      </div>
      <div className="micro">Payments at {b.rate.toFixed(2)}% over {b.amort_years} years{mode === "minimum" ? ", CMHC premium added to the mortgage" : ", no default insurance"}; income at the {b.qualifying_rate.toFixed(2)}% stress-test rate with property tax and heat included.{b.is_live ? "" : " Rate is an estimate until today's lender sync lands."} Source: {snap.source_label} · {snap.period_label}.</div>
    </div>
  );
}
const Row = ({ label, price, sc, bold }) => (
  <div className={`rt-market-row rt-market-row-labelled ${bold ? "prov" : ""}`} role="row" data-testid={`city-type-row-${label.split(" ")[0].toLowerCase()}`}>
    <span data-l="Property type">{bold ? <b>{label}</b> : label}</span><span data-l="Average price"><b>{money(price)}</b></span>
    <span data-l="Down payment">{money(sc.down)} <small>{sc.down_pct}%</small></span>
    <span data-l="Monthly payment" className="hl"><b>{money(sc.monthly_payment)}</b> <small>/mo</small></span><span data-l="Income to qualify">~{money(sc.income_needed)} <small>/yr</small></span>
  </div>
);

function CityScenarios({ data, C, openDrawer }) {
  if (!data.scenarios?.length) return null;
  return (
    <section className="rt-scen-sec" id="scenarios" data-testid="city-scenarios">
      <div className="wrap">
        <Intro eyebrow={fillPlace(HUB.scenarios.eyebrow, C)} heading={<>{HUB.scenarios.heading} <em>{HUB.scenarios.heading_em}</em></>} text={data.scenarios_intro || fillPlace(HUB.scenarios.text, C)} />
        <div className="rt-scen-grid">
          {data.scenarios.map((s, i) => (
            <article className="rt-scen-card" key={s.title} data-testid={`city-scenario-${i}`}>
              <span className="rt-scen-n">0{i + 1}</span><h3>{s.title}</h3><p>{s.text}</p>
              <button type="button" className="textlink" onClick={() => openDrawer({ entryCta: "city_scenario", presetLevel1: presetFor(s.title) })} data-testid={`city-scenario-cta-${i}`}>{HUB.scenarios.cta}</button>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// 12-month average-price sparkline from published AREA snapshots; hidden until 3+ months exist.
function Sparkline({ points }) {
  const pts = (points || []).filter((p) => p.avg_price);
  if (pts.length < 3) return null;
  const W = 280, H = 64, lo = Math.min(...pts.map((p) => p.avg_price)), hi = Math.max(...pts.map((p) => p.avg_price));
  const x = (i) => (i / (pts.length - 1)) * (W - 8) + 4, y = (v) => H - 6 - ((v - lo) / Math.max(1, hi - lo)) * (H - 12);
  const d = pts.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.avg_price).toFixed(1)}`).join(" ");
  const lbl = (per) => new Date(`${per}-15T12:00:00`).toLocaleDateString("en-CA", { month: "short" });
  return (
    <div className="rt-spark" data-testid="city-price-sparkline">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Average price, ${lbl(pts[0].period)} to ${lbl(pts[pts.length - 1].period)}`}><path d={d} /><circle cx={x(pts.length - 1)} cy={y(pts[pts.length - 1].avg_price)} r="4" /></svg>
      <div className="rt-spark-axis"><span>{lbl(pts[0].period)}</span><span>{pts.length} months</span><span>{lbl(pts[pts.length - 1].period)}</span></div>
    </div>
  );
}
